import React, { useState } from "react";
import PropTypes from "prop-types";
import { Alert, Button, Form, Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useIntl } from "react-intl";
import { faBookOpen, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import rest from "../../../../common/rest";
import { importLibrarySections } from "../../../../ducks/surveyEditorActions";
import { cleanText } from "../../../../utils/QuestionUtils";
import LibrarySavedAt from "../../../common/LibrarySavedAt";
import LibraryOrigin from "../../../common/LibraryOrigin";
import LibraryTable from "../../../common/LibraryTable";
import TrashButton from "../../../common/TrashButton";

const url = "/admin/library/sections/";
const columns = [
  { key: "name", label: "library.elementTitle", value: (item) => cleanText(item.name) },
  { key: "origin", label: "library.origin", value: (item) => `${item.surveyName || ""} ${item.version ?? ""}` },
  {
    key: "questions",
    label: "section.library.questions",
    compact: true,
    value: (item) => (item.section.questions || []).length
  },
  {
    key: "created",
    label: "library.savedAt",
    compact: true,
    value: (item) => (item.created == null ? 0 : new Date(item.created).getTime())
  }
];

export default function SectionLibrary({ disabled = false }) {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [show, setShow] = useState(false);
  const [sections, setSections] = useState([]);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const msg = (id) => intl.formatMessage({ id });

  async function request(operation) {
    setBusy(true);
    setError(false);
    try {
      await operation();
    } catch (reason) {
      setError(true);
    } finally {
      setBusy(false);
    }
  }

  function open() {
    setShow(true);
    setSelected([]);
    setDeleting(null);
    setSections([]);
    request(async () => setSections(await rest.doGet(url)));
  }

  const selection = sections.filter((section) => selected.includes(section.id));
  return (
    <>
      <Button className="text-start" onClick={open} disabled={disabled}>
        <FontAwesomeIcon icon={faBookOpen} className="me-2" />
        {msg("section.library.title")}
      </Button>
      <Modal size="xl" show={show} onHide={() => !busy && setShow(false)} scrollable>
        <Modal.Header closeButton={!busy}>
          <Modal.Title>{msg("section.library.title")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{msg("section.library.error")}</Alert>}
          {busy && (
            <p role="status" className="mt-3">
              {msg("section.library.loading")}
            </p>
          )}
          {!busy && !error && !sections.length && <p className="mt-3">{msg("section.library.empty")}</p>}
          {!!sections.length && (
            <LibraryTable
              items={sections}
              columns={columns}
              renderRow={(section) => (
                <tr key={section.id}>
                  <td>
                    <Form.Check
                      type="checkbox"
                      id={`library-section-${section.id}`}
                      aria-label={section.name}
                      checked={selected.includes(section.id)}
                      disabled={busy}
                      onChange={(event) => {
                        setSelected((ids) =>
                          event.target.checked ? [...ids, section.id] : ids.filter((id) => id !== section.id)
                        );
                        setDeleting(null);
                      }}
                    />
                  </td>
                  <td>{section.name}</td>
                  <td>
                    <LibraryOrigin name={section.surveyName} version={section.version} />
                  </td>
                  <td className="text-nowrap">
                    {(section.section.questions || []).length}
                    <OverlayTrigger
                      placement="auto"
                      overlay={
                        <Tooltip id={`library-section-questions-${section.id}`}>
                          <ol className="mb-0 ps-3 text-start">
                            {(section.section.questions || []).map((question, index) => (
                              <li key={index}>{cleanText(question.title || question.name)}</li>
                            ))}
                          </ol>
                        </Tooltip>
                      }
                    >
                      <Button variant="link" size="sm" className="p-0 ms-2" aria-label={msg("library.questionPreview")}>
                        <FontAwesomeIcon icon={faInfoCircle} />
                      </Button>
                    </OverlayTrigger>
                  </td>
                  <td className="text-nowrap">
                    <LibrarySavedAt value={section.created} />
                  </td>
                  <td>
                    <TrashButton disabled={busy} titleId="library.delete" onClick={() => setDeleting(section)} />
                  </td>
                </tr>
              )}
            />
          )}
          {deleting && (
            <Alert variant="warning">
              <p>{intl.formatMessage({ id: "section.library.deleteConfirm" }, { name: deleting.name })}</p>
              <Button
                variant="danger"
                disabled={busy}
                className="me-2"
                onClick={() =>
                  request(async () => {
                    await rest.doDelete(url + deleting.id);
                    setSections((items) => items.filter((item) => item.id !== deleting.id));
                    setSelected((ids) => ids.filter((id) => id !== deleting.id));
                    setDeleting(null);
                  })
                }
              >
                {msg("library.delete")}
              </Button>
              <Button variant="secondary" disabled={busy} onClick={() => setDeleting(null)}>
                {msg("app.common.Cancel")}
              </Button>
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" disabled={busy} onClick={() => setShow(false)}>
            {msg("app.common.Close")}
          </Button>
          <Button
            disabled={busy || !selection.length || !!deleting}
            onClick={() => {
              dispatch(importLibrarySections(selection));
              setShow(false);
            }}
          >
            {msg("library.insertSelected")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

SectionLibrary.propTypes = { disabled: PropTypes.bool };
